import React, { useCallback } from 'react';
import { AskRequest } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import RequestType, { mapAskRequestTypeToLabel } from 'presentation/components/KSQ2/Common/RequestType/RequestType';
import { Tooltip } from 'presentation/components/KSQ2/Common';
import { getShortId } from 'presentation/components/KSQ2/utils/shortId';
import './RequestIdentifier.scss';

interface RequestIdentifierProps {
  request: AskRequest;
  /** Whether the current user can manage queue (KS user vs client) */
  canManageQueue?: boolean;
}

/**
 * Displays the request type badge and short reference number
 * For clients (canManageQueue=false), the ID is hidden but shown in tooltip
 * Click to copy the ID in #0000 format
 */
export const RequestIdentifier: React.FC<RequestIdentifierProps> = ({ request, canManageQueue = true }) => {
  const prefix = prefixClass('conversation-header-request-id');
  const isGroup = request.type === 'Group';

  // Generate a short, human-readable ID from the UUID
  const shortId = getShortId(request.id);
  const formattedId = `#${shortId}`;
  const typeLabel = mapAskRequestTypeToLabel[request.type] || request.type;
  const typeSize = isGroup ? 'default' : 'small';

  const handleCopyId = useCallback(() => {
    navigator.clipboard.writeText(formattedId);
  }, [formattedId]);

  const tooltipContent = (
    <div className={prefix('tooltip')}>
      <div>
        ({shortId}) {typeLabel}
      </div>
      <div className={prefix('tooltip-hint')}>Click to copy ID</div>
    </div>
  );

  // For clients, hide the ID but show it in tooltip
  if (!canManageQueue) {
    return (
      <Tooltip content={tooltipContent} position="bottom" alignment="center">
        <div className={prefix()} onClick={handleCopyId}>
          <RequestType type={request.type} size={typeSize} showTooltip={false} />
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={tooltipContent} position="bottom" alignment="center">
      <div className={prefix()} onClick={handleCopyId}>
        <RequestType type={request.type} size={typeSize} showTooltip={false} />
        {!isGroup && <span className={prefix('number')}>{shortId}</span>}
      </div>
    </Tooltip>
  );
};

export default RequestIdentifier;
